import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import PageContainer from "../../components/PageContainer";
import VisitNotesPanel from "../../components/VisitNotesPanel";
import { DiagnosisAPI, DiagnosisApiError, PatientInfo, VisitResponse } from "../../services/diagnosis.service";

// Dedicated visit view — reachable directly at /visits/:visitId. Hosts the
// Visit Notes thread (visit-anchored, multi-doctor, versioned). Not yet
// linked from the visit workflow nav; navigate here by URL for now.

const VisitNotesPage: React.FC = () => {
  const { visitId } = useParams<{ visitId: string }>();
  const [visit, setVisit] = useState<VisitResponse | null>(null);
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visitId) { setLoading(false); return; }
    let cancelled = false;

    DiagnosisAPI.getVisit(visitId)
      .then(async ({ visit: v }) => {
        if (cancelled) return;
        setVisit(v);
        try {
          const p = await DiagnosisAPI.getPatient(v.patient_id);
          if (!cancelled) setPatient(p);
        } catch {
          /* patient context is nice-to-have; notes still render without it */
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof DiagnosisApiError ? e.message : "Failed to load visit");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [visitId]);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar />
      <div className="flex-1 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 flex flex-col min-h-screen">
        <AppHeader title="Visit" />
        <div className="p-4 flex flex-1">
          <PageContainer className="flex flex-col gap-4 max-w-3xl mx-auto w-full">
            {!visitId && (
              <p className="text-sm text-red-500 text-center py-8">No visit specified.</p>
            )}

            {visitId && loading && (
              <p className="text-sm text-slate-400 text-center py-8">Loading visit…</p>
            )}

            {visitId && !loading && error && (
              <p className="text-sm text-red-500 text-center py-8">{error}</p>
            )}

            {visitId && !loading && !error && (
              <>
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-4 py-4">
                  <h1 className="text-lg font-bold text-slate-900">
                    {patient ? `${patient.first_name} ${patient.last_name}` : "Visit"}
                  </h1>
                  {visit && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {new Date(visit.visit_date).toLocaleDateString("en-IN", {
                        day: "2-digit", month: "short", year: "numeric",
                      })}
                      {" · "}
                      {visit.status}
                    </p>
                  )}
                </div>

                <VisitNotesPanel visitId={visitId} />
              </>
            )}
          </PageContainer>
        </div>
      </div>
    </div>
  );
};

export default VisitNotesPage;
